import {
  keepPreviousData,
  QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useCallback } from 'react';
import { PAGINATION_PAGE_SIZE_LARGE } from '~/constants';

import { queryKeysAnalytics } from '../queryKeys';
import { fetchTopAccountingReferencesBreakdown } from '../useQueryTopAccountingReferencesBreakdown';
import {
  TopAccountingReferencesBreakdownQueryParams,
  TopAccountingReferencesBreakdownResponse,
} from '../useQueryTopAccountingReferencesBreakdown/types';
import { zodTopAccountingReferencesBreakdownQueryParamsSchema } from '../useQueryTopAccountingReferencesBreakdown/validations';

/**
 * Generates the query options for the top accounting references (aka cost centers) signature rates query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching the top accounting references breakdown.
 * @param options - Additional options for the query.
 * @param ignoreSignal - If true, the abort signal is not passed to the fetch function.
 *   Use this for prefetching in route loaders to prevent request cancellation when the loader completes.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getTopAccountingReferencesBreakdownQueryOptions = ({
  queryParams,
  options,
  ignoreSignal = false,
}: {
  queryParams: TopAccountingReferencesBreakdownQueryParams;
  options?: Omit<
    UseQueryOptions<
      TopAccountingReferencesBreakdownResponse,
      Error,
      TopAccountingReferencesBreakdownResponse
    >,
    'queryKey' | 'queryFn'
  >;
  ignoreSignal?: boolean;
}) => {
  const validatedQueryParams =
    zodTopAccountingReferencesBreakdownQueryParamsSchema.parse(queryParams);

  const isEnabled = Boolean(
    validatedQueryParams.dateRange.dateFrom &&
    validatedQueryParams.dateRange.dateTo,
  );

  return {
    enabled: isEnabled,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchTopAccountingReferencesBreakdown(validatedQueryParams, {
        signal: ignoreSignal ? undefined : signal,
      }),
    queryKey:
      queryKeysAnalytics.getTopAccountingReferencesBreakdown(
        validatedQueryParams,
      ),
    staleTime: ms('5m'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the top accounting references (aka cost centers) signature rates data with given query parameters.
 *
 * @param queryParams - The query parameters for fetching the top accounting references signature rates.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Accounting-Reference/operation/get_top_accounting_references_breakdown_accounting_reference_statistics_top_accounting_references_post
 */
export const useQueryTopAccountingReferencesBreakdown = (
  queryParams: TopAccountingReferencesBreakdownQueryParams,
  options?: Omit<
    UseQueryOptions<
      TopAccountingReferencesBreakdownResponse,
      Error,
      TopAccountingReferencesBreakdownResponse
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getTopAccountingReferencesBreakdownQueryOptions({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });

/**
 * Generates the infinite query options for prefetching the first page of top accounting references breakdown.
 *
 * @param queryParams - The query parameters for fetching the top accounting references breakdown.
 * @param ignoreSignal - If true, the abort signal is not passed to the fetch function.
 * @returns The infinite query options for prefetching.
 */
export const getTopAccountingReferencesBreakdownInfiniteQueryOptions = ({
  queryParams,
  ignoreSignal = false,
}: {
  queryParams: Partial<TopAccountingReferencesBreakdownQueryParams>;
  ignoreSignal?: boolean;
}) => {
  const validatedQueryParams =
    zodTopAccountingReferencesBreakdownQueryParamsSchema.parse(queryParams);

  return {
    getNextPageParam: (lastPage: TopAccountingReferencesBreakdownResponse) => {
      const nextOffset = (lastPage.offset ?? 0) + (lastPage.data?.length ?? 0);

      return nextOffset < (lastPage.totalCount ?? 0) ? nextOffset : undefined;
    },
    initialPageParam: 0,
    queryFn: async ({
      pageParam: pageParameter = 0,
      signal,
    }: QueryFunctionContext) =>
      fetchTopAccountingReferencesBreakdown(
        {
          ...validatedQueryParams,
          offset: pageParameter,
        },
        { signal: ignoreSignal ? undefined : signal },
      ),
    queryKey:
      queryKeysAnalytics.getTopAccountingReferencesBreakdownInfinite(
        validatedQueryParams,
      ),
    staleTime: ms('5m'),
  };
};

/**
 * React Query based custom hook for windowed/sparse data fetching of accounting references by signature rate.
 * Unlike useInfiniteQuery, this approach only fetches pages for currently visible items,
 * allowing users to jump to any position without loading all intermediate data.
 *
 * @param queryParams - The query parameters for fetching accounting references by signature rate.
 * @returns An object containing:
 *   - fetchPage: Function to fetch a specific page by offset
 *   - queryKey: The base query key for cache management
 *   - pageSize: The page size being used
 */
export const useQueryTopAccountingReferencesBreakdownWindowed = (
  queryParams: Partial<TopAccountingReferencesBreakdownQueryParams>,
) => {
  const validatedQueryParams =
    zodTopAccountingReferencesBreakdownQueryParamsSchema.parse(queryParams);

  const pageSize = validatedQueryParams.limit ?? PAGINATION_PAGE_SIZE_LARGE;

  // Stable fetchPage function for the windowed component
  const fetchPage = useCallback(
    async (offset: number, signal: AbortSignal) => {
      return fetchTopAccountingReferencesBreakdown(
        { ...validatedQueryParams, offset },
        { signal },
      );
    },
    [
      validatedQueryParams.dateRange.dateFrom,
      validatedQueryParams.dateRange.dateTo,
      validatedQueryParams.sortBy,
      validatedQueryParams.sortOrder,
      validatedQueryParams.signatureFilter,
      validatedQueryParams.limit,
    ],
  );

  return {
    fetchPage,
    pageSize,
    queryKey:
      queryKeysAnalytics.getTopAccountingReferencesBreakdown(
        validatedQueryParams,
      ),
  };
};
